import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Headset, Lock, MapPin, Phone, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FeedbackMessage } from "@/components/ui/feedback";
import { Input, Label } from "@/components/ui/input";
import { Header } from "@/components/layout/Header";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/features/auth/useAuth";
import { ROLE_HOME, ROLE_LABEL, type Role } from "@/features/auth/types";
import { isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import { addressText, company } from "@/content/site";

const highlights = [
  { icon: ShieldCheck, text: "Secure, role-based access for staff, drivers, and partners" },
  { icon: Eye, text: "Live load tracking and fleet status in one place" },
  { icon: Headset, text: "Dispatch that answers - day or night" },
];

export default function Login() {
  const { user, signIn } = useAuth();
  const location = useLocation();
  const isRecoveryLink =
    window.location.hash.includes("type=recovery") ||
    window.location.search.includes("type=recovery");
  const [mode, setMode] = useState<"signIn" | "forgot" | "reset">(
    isRecoveryLink ? "reset" : "signIn",
  );
  const [email, setEmail] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [devRole, setDevRole] = useState<Role>("customer");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (user && mode !== "reset") {
    const dest = (location.state as { from?: string })?.from ?? ROLE_HOME[user.role];
    return <Navigate to={dest} replace />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await signIn(email, password, devRole);
    } catch {
      setError("Invalid email or password. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function onForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);

    if (!isSupabaseConfigured || !supabase) {
      setNotice("Password reset email will be available after Supabase Auth is connected.");
      return;
    }
    if (!resetEmail.trim()) {
      setError("Enter the email address for this account.");
      return;
    }

    setBusy(true);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        resetEmail.trim().toLowerCase(),
        {
          redirectTo: `${window.location.origin}/app?type=recovery`,
        },
      );
      if (resetError) throw resetError;
      setNotice("If that email has an account, a password reset link has been sent.");
    } catch {
      setError("Could not send the reset email. Please try again or contact dispatch.");
    } finally {
      setBusy(false);
    }
  }

  async function onUpdatePassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);

    if (!isSupabaseConfigured || !supabase) {
      setError("Password reset is available after Supabase Auth is connected.");
      return;
    }
    if (newPassword.length < 10) {
      setError("Password must be at least 10 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;
      await supabase.auth.signOut();
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setMode("signIn");
      setNotice("Password updated. Please sign in with the new password.");
      window.history.replaceState({}, document.title, "/app");
    } catch {
      setError("Could not update password. The reset link may be expired - request a new one.");
    } finally {
      setBusy(false);
    }
  }

  const phoneHref = `tel:${company.phone.replace(/[^+\d]/g, "")}`;

  return (
    <>
      <Seo title="Sign in" noindex />
      <div className="flex min-h-dvh min-w-0 flex-col">
      <Header />
      <div className="grid min-w-0 flex-1 lg:grid-cols-2">
        {/* Brand showcase (left) */}
        <aside className="relative hidden flex-col justify-center overflow-hidden bg-brand-900 p-10 text-white lg:flex xl:p-14">
          <img
            src="/brand/hero-truck.jpg"
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover opacity-25"
          />
          <div
            className="absolute inset-0 bg-gradient-to-br from-brand-900/95 via-brand-900/80 to-brand-900/95"
            aria-hidden
          />

          <div className="relative w-full max-w-md">
            <h2 className="text-3xl font-bold leading-tight tracking-tight xl:text-4xl">
              {company.tagline}
            </h2>
            <ul className="mt-8 space-y-4">
              {highlights.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-3 text-brand-100">
                  <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-accent-500/20 text-accent-400">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="text-sm leading-relaxed">{text}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 space-y-1 border-t border-white/10 pt-6 text-sm text-brand-200">
              <a href={phoneHref} className="flex items-center gap-2 hover:text-white">
                <Phone className="h-4 w-4" /> {company.phone}
              </a>
              <p className="flex items-center gap-2">
                <MapPin className="h-4 w-4" /> {addressText}
              </p>
            </div>
          </div>
        </aside>

        {/* Sign-in form (right) */}
        <div className="flex flex-col bg-brand-50">
          <main className="flex flex-1 items-center justify-center px-4 py-8 sm:py-12">
            <div className="w-full max-w-md min-w-0">
              <div className="mb-6">
                <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/10 px-3 py-1 text-xs font-semibold text-accent-600">
                  <Lock className="h-3.5 w-3.5" /> Secure portal
                </span>
                <h1 className="mt-4 text-2xl font-bold tracking-tight text-brand-900 sm:text-3xl">
                  {mode === "forgot"
                    ? "Reset your password"
                    : mode === "reset"
                      ? "Set a new password"
                      : "Welcome back"}
                </h1>
                <p className="mt-2 break-words text-sm text-brand-500">
                  {mode === "forgot"
                    ? "Enter your account email and we will send a secure reset link."
                    : mode === "reset"
                      ? "Choose a new password for your portal account."
                      : `Sign in to access your ${company.shortName} dashboard.`}
                </p>
              </div>

              <div className="min-w-0 rounded-2xl border border-brand-100 bg-white p-5 shadow-sm sm:p-8">
                {mode === "signIn" && (
                  <form onSubmit={onSubmit} className="space-y-5">
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@bullandbearco.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <Label htmlFor="password">Password</Label>
                        <button
                          type="button"
                          className="text-xs font-medium text-accent-600 hover:underline"
                          onClick={() => {
                            setError(null);
                            setNotice(null);
                            setResetEmail(email);
                            setMode("forgot");
                          }}
                        >
                          Forgot password?
                        </button>
                      </div>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          autoComplete="current-password"
                          className="pr-10"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required={isSupabaseConfigured}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          className="absolute inset-y-0 right-0 grid w-10 place-items-center text-brand-400 hover:text-brand-700"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    {!isSupabaseConfigured && (
                      <div className="rounded-lg border border-dashed border-brand-200 bg-brand-50 p-3">
                        <Label htmlFor="devRole">Preview portal as (dev only)</Label>
                        <select
                          id="devRole"
                          className="mt-1 flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm"
                          value={devRole}
                          onChange={(e) => setDevRole(e.target.value as Role)}
                        >
                          {(Object.keys(ROLE_LABEL) as Role[]).map((r) => (
                            <option key={r} value={r}>
                              {ROLE_LABEL[r]}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-brand-400">
                          Auth is not configured yet - this lets you preview each portal.
                        </p>
                      </div>
                    )}

                    <FeedbackMessage tone="error">{error}</FeedbackMessage>
                    <FeedbackMessage tone="success">{notice}</FeedbackMessage>

                    <Button type="submit" variant="accent" className="w-full" disabled={busy}>
                      {busy ? "Signing in..." : "Sign in"}
                    </Button>
                  </form>
                )}

                {mode === "forgot" && (
                  <form onSubmit={onForgotPassword} className="space-y-5">
                    <div>
                      <Label htmlFor="reset-email">Account email</Label>
                      <Input
                        id="reset-email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@bullandbearco.com"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                      />
                    </div>

                    <FeedbackMessage tone="error">{error}</FeedbackMessage>
                    <FeedbackMessage tone="success">{notice}</FeedbackMessage>

                    <Button type="submit" variant="accent" className="w-full" disabled={busy}>
                      {busy ? "Sending..." : "Send reset link"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setError(null);
                        setNotice(null);
                        setMode("signIn");
                      }}
                    >
                      Back to sign in
                    </Button>
                  </form>
                )}

                {mode === "reset" && (
                  <form onSubmit={onUpdatePassword} className="space-y-5">
                    <div>
                      <Label htmlFor="new-password">New password</Label>
                      <Input
                        id="new-password"
                        type="password"
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirm-password">Confirm password</Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                      />
                    </div>

                    <FeedbackMessage tone="error">{error}</FeedbackMessage>
                    <FeedbackMessage tone="success">{notice}</FeedbackMessage>

                    <Button type="submit" variant="accent" className="w-full" disabled={busy}>
                      {busy ? "Updating..." : "Update password"}
                    </Button>
                  </form>
                )}
              </div>

              <p className="mt-6 text-center text-sm text-brand-500">
                Need access or trouble signing in?{" "}
                <a href={phoneHref} className="font-medium text-accent-600 hover:underline">
                  Contact dispatch
                </a>
              </p>
            </div>
          </main>
        </div>
      </div>
      </div>
    </>
  );
}
